import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { useIsFeatureFlagEnabled } from '@paradigm/features/src/feature-flags/store';
import { selectIsAuthenticated } from '@paradigm/store/auth';
import { UnifiedRfqTradeTapeProvider } from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';

import {
  FEATURE_BLOCK_TERMINAL,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';

import BlockTerminalTab from '#/components/Terminal/BlockTerminalTab';
import {
  CHAT_PANE_WIDTH,
  SIDEBAR_WIDTH,
} from '#/components/Terminal/layout-constants';
import { LocalTabFiltersProvider } from '#/components/UnifiedRfqs/UnifiedRfqs';

/**
 * Right-rail mount of the Block Terminal. Renders only when the user has
 * pinned the terminal as a side panel and the drawer is open. Both the
 * bottom-panel tabs and the side panel are visible simultaneously, similar
 * to the Paradex layout.
 *
 * `UnifiedRfqProviders` is mounted once at the App level by
 * `ScopedUnifiedRfqProviders`, above both `<Routes />` and this panel.
 * That's the only way to share a single provider tree across two sibling
 * subtrees without duplicating `DrfqNotificationsService` and firing
 * every fill toast twice.
 *
 * The auth gate matches `ScopedUnifiedRfqProviders`: drawer state
 * persists in localStorage across logout, so without this check the
 * panel would still render on the sign-in screen and crash trying to
 * consume UM contexts that only exist on the authenticated branch of
 * `LayoutContainer`.
 */
// Keep the chat area at its original width regardless of whether
// the sessions sidebar is open — the panel grows by `SIDEBAR_WIDTH`
// to fit the sidebar instead of stealing space from the chat.
// Matches the drawer-resize behaviour on Paradex. Both constants are
// shared with `BlockTerminalTab` via `layout-constants.ts` so the two
// can't drift out of sync.

export default function BlockTerminalSidePanel() {
  const enabled = useIsFeatureFlagEnabled(FEATURE_BLOCK_TERMINAL);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { isOpen, pinMode, sidebarCollapsed } = useTerminalDrawerState();

  if (!isAuthenticated) return null;
  if (!enabled) return null;
  if (pinMode !== 'side-panel') return null;
  if (!isOpen) return null;

  return (
    <Panel $sidebarOpen={!sidebarCollapsed}>
      <LocalTabFiltersProvider storageKey="trade-tape-rfq-tab-filters">
        <UnifiedRfqTradeTapeProvider>
          <BlockTerminalTab />
        </UnifiedRfqTradeTapeProvider>
      </LocalTabFiltersProvider>
    </Panel>
  );
}

const Panel = styled.aside<{ $sidebarOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${(p) =>
    p.$sidebarOpen ? CHAT_PANE_WIDTH + SIDEBAR_WIDTH : CHAT_PANE_WIDTH}px;
  max-width: 100vw;
  background: var(--bg-color-container, #0e131a);
  border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
  display: flex;
  flex-direction: column;
  z-index: 900;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.35);
  transition: width 0.18s ease;
`;
